/**
 * Top bar. SPEC.md §48, §20.2, §7.1.
 *
 * One row, and it stays one row: the entry zone owns at least 65% of the screen
 * and everything here sits at the bottom of the priority list — code, connection,
 * history, host menu. §7.1, §20.2, §18.2
 *
 * The code and the dot share a baseline. They used to be stacked, which cost the
 * bar a second line for a 8 px circle and pushed it into the roster's share of
 * the screen — and the two are one fact anyway: *this* table, and whether this
 * phone is still talking to it. The code is the larger of the two because it is
 * the thing a player reads out loud across the room. §48, §18.2
 *
 * The dot is all a healthy connection gets. When the transport is actually
 * unhappy, ReconnectOverlay does the explaining and the blocking. §16.2
 */

import { formatJoinCode, type ConnectionState } from '@card-counter/shared';
import { IconButton } from '@/components/ui/IconButton';
import { useConnectionState } from '@/stores/connection-store';
import { useIsHost, useSessionInfo } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

/** Colour reinforces the wording; it never carries the meaning alone. §19, §20.3 */
const STATUS: Record<ConnectionState, { label: string; dotClass: string; silent: boolean }> = {
  connected: { label: 'Connected', dotClass: 'bg-positive', silent: true },
  reconnecting: { label: 'Reconnecting…', dotClass: 'bg-warn', silent: false },
  resyncing: { label: 'Syncing…', dotClass: 'bg-warn', silent: false },
  offline: { label: 'Offline', dotClass: 'bg-negative', silent: false },
};

export function TopBar() {
  const session = useSessionInfo();
  const isHost = useIsHost();
  const connection = useConnectionState();
  const openPlayers = useUiStore((state) => state.openPlayers);
  const openHistory = useUiStore((state) => state.openHistory);
  const openHostMenu = useUiStore((state) => state.openHostMenu);

  const status = STATUS[connection];

  /**
   * The gear is the host menu and nothing else. V1 has no per-player settings
   * beyond the join form, so a non-host would get a control that opens nothing;
   * the lobby carries its own host actions and an ended table is read-only.
   * §33, §32.1, §49
   */
  const showHostMenu = isHost && session?.status === 'active';

  return (
    <header className="flex shrink-0 items-center gap-1 px-safe pt-safe">
      {/* One line: the code, then the dot immediately to the right of it on the
          same baseline. `items-center` rather than `items-baseline`, because the
          dot is a circle and a circle sits on a text baseline looking dropped. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {session ? (
          <p
            data-numeric
            className="shrink-0 truncate text-base leading-none font-semibold tracking-[0.14em] text-ink"
          >
            {formatJoinCode(session.joinCode)}
          </p>
        ) : null}

        <p
          role="status"
          aria-live="polite"
          className="flex min-w-0 items-center gap-1.5 text-xs leading-none text-ink-muted"
        >
          <span
            aria-hidden="true"
            className={`size-2.5 shrink-0 rounded-full ${status.dotClass}`}
          />
          {status.silent ? (
            <span className="sr-only">{status.label}</span>
          ) : (
            <span className="truncate">{status.label}</span>
          )}
          {/* §27.1 — locked only stops new joins, so it is a note, not a warning. */}
          {session?.locked ? (
            <>
              <span aria-hidden="true">🔒</span>
              <span className="sr-only">Table locked</span>
            </>
          ) : null}
        </p>
      </div>

      {/*
        The two overlays, and the only route to either. They are icons in the
        corner rather than a tab bar across the bottom because the bottom of this
        screen is where the commit actions live, and a permanent bar there would
        cost the keypad a row of keys to reach two screens nobody looks at while
        a hand is being played. §7.1, §18.2, §20.2
      */}
      <IconButton label="Players" onClick={openPlayers}>
        👥
      </IconButton>

      <IconButton label="History" onClick={openHistory}>
        🕑
      </IconButton>

      {showHostMenu ? (
        <IconButton label="Host controls" onClick={openHostMenu}>
          ⚙️
        </IconButton>
      ) : null}
    </header>
  );
}
